import { Member, ShareRole } from "@zapier/bff";
import {
  HydratedAssetShare,
  useDeleteAssetShare,
  useUpdateAssetShare,
} from "@zapier/client";
import { useShareEntityProps, ShareEntity } from "./ShareEntity";
import { useNotification } from "@zapier/utils";
import { useCallback, useMemo } from "react";
import { ShareAccessSelect } from "./ShareAccessSelect";
import { SkeletonBlock } from "@zapier/design-system";
import styles from "./ShareList.module.css";

interface Props {
  isDisabled?: boolean;
  roles: ShareRole[];
  share: HydratedAssetShare;
  onChangeOwner?: (newOwner: Member) => void;
  canChangeOwner?: boolean;
}

export function ShareListItem({
  isDisabled,
  roles,
  share,
  onChangeOwner,
  canChangeOwner,
}: Props) {
  const entityProps = useShareEntityProps(share);

  const { createNotification } = useNotification();
  const { mutateAsync: deleteShare } = useDeleteAssetShare();
  const { mutateAsync: updateShare } = useUpdateAssetShare();

  const allowChangeOwner = useMemo(() => {
    return share.subjectType === "user" && canChangeOwner && onChangeOwner;
  }, [share]);

  const rolesForSelect = useMemo(() => {
    if (share.role && !roles.includes(share.role)) {
      return [...roles, share.role];
    }
    return roles;
  }, [roles, share.role]);

  const handleRemoveAccess = useCallback(async () => {
    if (!entityProps) {
      console.warn("No entity props available for share removal.");
      return;
    }

    try {
      await deleteShare(share);
      createNotification({
        type: "success",
        message: `Removed access for ${entityProps.name}.`,
      });
    } catch (error) {
      console.error("Error while trying to delete share:", error);
      createNotification({
        type: "error",
        message: `Failed to remove access for ${entityProps.name}. The error was: ${error}`,
      });
    }
  }, [deleteShare, entityProps, share, createNotification]);

  const handleMakeOwner = useCallback(async () => {
    if (!entityProps || share.subjectType !== "user" || !share.user) {
      return;
    }
    onChangeOwner?.(share.user);
  }, [entityProps, share, onChangeOwner]);

  const handleUpdateAccess = useCallback(
    async (newRole: ShareRole) => {
      if (!entityProps) {
        console.warn("No entity props available for share update.");
        return;
      }

      try {
        await updateShare({ ...share, role: newRole });
        createNotification({
          type: "success",
          message: `Updated access for ${entityProps.name}.`,
        });
      } catch (error) {
        console.error("Error while trying to update share:", error);
        createNotification({
          type: "error",
          message: `Failed to update access for ${entityProps.name}. The error was: ${error}`,
        });
      }
    },
    [entityProps, share, updateShare, createNotification],
  );

  if (!entityProps) {
    return;
  }

  return (
    <li className={styles.listItem}>
      <ShareEntity {...entityProps} />

      <div className={styles.accessControl}>
        <ShareAccessSelect
          isDisabled={isDisabled}
          additionalActions={{
            ["Remove access"]: handleRemoveAccess,
            ...(allowChangeOwner && { ["Make owner"]: handleMakeOwner }),
          }}
          onSelectionChange={handleUpdateAccess}
          roles={rolesForSelect}
          selectedRole={share.role}
        />
      </div>
    </li>
  );
}

export function ShareListItemLoading() {
  return (
    <li className={styles.listItem}>
      <SkeletonBlock height={36} width="100%" />
    </li>
  );
}
